import { prescriptionIdentifier } from "@/utils/prescriptionIdentity.js";
import supabase from "@/services/supabaseClient.js";
import { resolvePatientAvatar } from "@/services/doctorPrescriptions.js";
import { getSupabaseBaseUrl } from "@/services/supabaseClient";
import {
  hydratePatientProfileAvatar,
  loadPatientIdentityContextsByAppointmentId,
  PATIENT_AVATAR_PLACEHOLDER,
} from "@/services/patientIdentityService.js";
import { callFunctionRaw, resolveFunctionAccessToken } from "@/lib/callFunction.js";
import {
  normalizeInventoryPrice,
  resolveBranchCurrency,
} from "@/lib/pharmacyCurrencyIntegrity.mjs";
import { normalizeCurrencyCode } from "@/services/marketPaymentService.js";
import { formatCurrency } from "@/utils/formatCurrency.js";
import {
  checkStagingAccess,
  getRuntimeHostname,
  isStagingRuntimeHost,
  normalizeStagingEmail,
  readCachedStagingAccess,
} from "@/services/stagingAccessService.js";

export const PHARMACY_PROFILE_STORAGE_KEY = "afrimed:pharmacy:profile:v1";
export const PHARMACY_PROFILE_CHANGED_EVENT = "afrimed:pharmacy:profile:changed";
export const PHARMACY_NOTIFICATIONS_STORAGE_KEY = "afrimed:pharmacy:notifications:v1";
export const PHARMACY_NOTIFICATIONS_CHANGED_EVENT = "afrimed:pharmacy:notifications:changed";
export const PHARMACY_MEMBER_FLASH_KEY = "afrimed:pharmacy:members:flash";
export const PHARMACY_STOCK_PLACEHOLDER = "/icons/pharmacy/stock-placeholder.svg";
export const PHARMACY_EXPAND_MORE_ICON = "/icons/appointments/dashboard/expand_more.svg";
export const PHARMACY_ONBOARDED_STORAGE_KEY = "afrimed:pharmacy:onboarded:v1";
const PHARMACY_ACCESS_CACHE_KEY = "afrimed:pharmacy:access:v1";
const PHARMACY_ACCESS_CACHE_TTL_MS = 2 * 60 * 1000;
const PHARMACY_ACCESS_TIMEOUT_MS = 5000;
const PHARMACY_STAFF_MEMBERS_TABLE = "pharmacy_staff_members";
export const PHARMACY_MEMBER_ROLE_OPTIONS = [
  {
    value: "owner",
    label: "Owner",
    note: "Full branch control, pharmacy settings, payout settings, staff access, and all branch fulfilment activity.",
  },
  {
    value: "dispensing-pharmacist",
    label: "Dispensing pharmacist",
    note: "Review incoming prescriptions, confirm stock, prepare orders, dispense medication, and update fulfilment status. Cannot change ownership, remove the pharmacy, manage payment settings, or invite staff.",
  },
  {
    value: "inventory-manager",
    label: "Inventory manager",
    note: "Maintain stock, supplier notes, low-stock actions, and stock availability signals. Cannot manage ownership, payment settings, or staff access.",
  },
  {
    value: "finance-lead",
    label: "Finance lead",
    note: "Track settlement exports, fee visibility, and reconciliation. Cannot change branch ownership, remove staff, or perform clinical dispensing handoff.",
  },
  {
    value: "delivery-lead",
    label: "Delivery lead",
    note: "Coordinate dispatch, verified addresses, delivery readiness, and patient handoff logistics. Cannot manage payment settings, ownership, or staff invitations.",
  },
  {
    value: "branch-admin",
    label: "Branch admin",
    note: "Support day-to-day branch operations and team coordination. This is an assistant/staff role for staging and should not manage ownership, payment settings, or staff invitations.",
  },
];

export const PHARMACY_ROLE_PERMISSION_GROUPS = [
  {
    value: "owner",
    title: "Owner privileges",
    copy: "Can manage the branch profile, verification details, delivery zones, staff invitations, role assignments, fulfilment activity, pricing or quotes, and pharmacy settings including payout setup.",
  },
  {
    value: "branch-admin",
    title: "Assistant / branch staff",
    copy: "Can support routine branch operations and fulfilment handoff in staging. Cannot manage ownership, payment settings, pharmacy removal, or staff invitations.",
  },
  {
    value: "dispensing-pharmacist",
    title: "Dispensing pharmacist",
    copy: "Can review branch prescriptions, confirm stock, prepare or dispense medication, approve substitutions where appropriate, and update fulfilment status. Cannot manage ownership, staff access, or payment settings.",
  },
  {
    value: "inventory-manager",
    title: "Inventory manager",
    copy: "Can maintain stock counts, reorder thresholds, cold-chain checks, supplier references, and stock availability signals. Cannot manage owners, staff access, or payment settings.",
  },
  {
    value: "finance-lead",
    title: "Finance lead",
    copy: "Can view settlement exports, fee visibility, and reconciliation support. Cannot change ownership, invite staff, or complete clinical dispensing handoff.",
  },
  {
    value: "delivery-lead",
    title: "Delivery lead",
    copy: "Can coordinate dispatch, verified addresses, delivery readiness, and handoff logistics. Cannot manage ownership, payment settings, staff access, or stock rules.",
  },
];

export function getPharmacyRolePermissionCopy(roleValue = "") {
  const normalized = String(roleValue || "").trim();
  const option =
    PHARMACY_MEMBER_ROLE_OPTIONS.find((item) => item.value === normalized) ||
    PHARMACY_MEMBER_ROLE_OPTIONS.find((item) => item.value === "branch-admin");
  const group =
    PHARMACY_ROLE_PERMISSION_GROUPS.find((item) => item.value === normalized) ||
    PHARMACY_ROLE_PERMISSION_GROUPS.find((item) => item.value === "branch-admin");

  return {
    roleValue: option?.value || "branch-admin",
    label: option?.label || "Branch admin",
    title: group?.title || option?.label || "Branch admin",
    summary: option?.note || "",
    detail: group?.copy || option?.note || "",
  };
}

const IMAGE_POOL = [
  "https://www.figma.com/api/mcp/asset/3ac7080a-c9d5-444a-a342-09098160c4f2",
  "https://www.figma.com/api/mcp/asset/c37f0991-4c38-4013-8a76-29b79ba3d9dc",
  "https://www.figma.com/api/mcp/asset/fcb26102-cf20-4a52-a984-df38d47412e1",
  "https://www.figma.com/api/mcp/asset/5ff32781-ce48-4fc2-8181-cd7608faf84f",
  "https://www.figma.com/api/mcp/asset/9bc20c01-72d9-41e5-8b0c-9a0b9e550229",
];

const DOCTOR_AVATAR_BUCKET = "doctor-profile-images";

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

function withPharmacyTimeout(promise, ms = PHARMACY_ACCESS_TIMEOUT_MS, label = "Pharmacy request") {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error(`${label} timed out`)), ms);
    }),
  ]);
}

function parseNumber(value) {
  const raw = String(value ?? "")
    .replace(/[^\d.-]/g, "")
    .trim();
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function asRecord(value) {
  return value && typeof value === "object" ? value : {};
}

function pickFirst(...values) {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
}

function resolvePublicStorageUrl(bucket, rawPath) {
  const path = String(rawPath || "").trim();
  if (!path) return "";
  if (/^(?:https?:|data:|blob:)/i.test(path)) return path;

  const base = getSupabaseBaseUrl();
  const cleanPath = path.replace(/^public\//, "").replace(/^\/+/, "");
  if (!base || !cleanPath) return "";

  return `${base}/storage/v1/object/public/${bucket}/${cleanPath}`;
}

function resolveDoctorLikeAvatar(record) {
  if (!record || typeof record !== "object") return "";

  return pickFirst(
    record?.resolved_avatar_url,
    record?.avatar_url,
    record?.profile_image_url,
    record?.profile_photo_url,
    record?.photo_url,
    resolvePublicStorageUrl(
      DOCTOR_AVATAR_BUCKET,
      pickFirst(
        record?.profile_photo_path,
        record?.profile_photo_storage_path,
        record?.photo_path,
        record?.avatar_path,
      ),
    ),
  );
}

function pickResolvedAvatar(...values) {
  for (const value of values) {
    const avatar = String(value || "").trim();
    if (!avatar) continue;
    if (avatar === PATIENT_AVATAR_PLACEHOLDER) continue;
    return avatar;
  }
  return "";
}

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizePhoneForCompare(value) {
  return String(value || "").replace(/[^\d+]/g, "").trim();
}

function buildPharmacyAccessCacheEntryKey({ userId = "", email = "" } = {}) {
  const cleanUserId = String(userId || "").trim();
  const cleanEmail = normalizeEmail(email);
  return `${cleanUserId}::${cleanEmail}`;
}

function readPharmacyAccessCache() {
  return readJson(PHARMACY_ACCESS_CACHE_KEY, {});
}

function readCachedPharmacyAccess({ userId = "", email = "" } = {}) {
  const key = buildPharmacyAccessCacheEntryKey({ userId, email });
  if (!key) return null;

  const cache = asRecord(readPharmacyAccessCache());
  const entry = asRecord(cache[key]);
  const writtenAt = Number(entry.writtenAt || 0);
  if (!writtenAt || Date.now() - writtenAt > PHARMACY_ACCESS_CACHE_TTL_MS) return null;

  return {
    allowed: entry.allowed === true,
    rows: Array.isArray(entry.rows) ? entry.rows : [],
    hasOwnerProfile: entry.hasOwnerProfile === true,
  };
}

function writeCachedPharmacyAccess({ userId = "", email = "", value = null } = {}) {
  const key = buildPharmacyAccessCacheEntryKey({ userId, email });
  if (!key || !value) return;

  const cache = asRecord(readPharmacyAccessCache());
  cache[key] = {
    allowed: value.allowed === true,
    rows: Array.isArray(value.rows) ? value.rows : [],
    hasOwnerProfile: value.hasOwnerProfile === true,
    writtenAt: Date.now(),
  };
  writeJson(PHARMACY_ACCESS_CACHE_KEY, cache);
}

function normalizeMemberStatus(row = {}, roleValue = "") {
  if (roleValue === "owner") return "active";

  const explicit = normalizeText(row.status);
  if (explicit === "revoked" || explicit === "removed") {
    return "revoked";
  }
  if (
    pickFirst(
      row.userId,
      row.user_id,
      row.linkedUserId,
      row.linked_user_id,
      row.acceptedAt,
      row.accepted_at,
      row.joinedAt,
      row.joined_at,
    )
  ) {
    return "active";
  }
  if (explicit === "active" || explicit === "accepted" || explicit === "joined") {
    return "active";
  }
  if (explicit === "invited" || explicit === "pending") {
    return "invited";
  }

  if (normalizeText(row.source).includes("invite")) {
    return "invited";
  }

  return "active";
}

function toRoleValue(value) {
  const normalized = normalizeText(value);
  if (!normalized) return "branch-admin";
  if (normalized.includes("owner") || normalized.includes("superintendent")) return "owner";
  if (normalized.includes("dispensing") || normalized.includes("pharmacist")) {
    return "dispensing-pharmacist";
  }
  if (normalized.includes("inventory") || normalized.includes("stock")) return "inventory-manager";
  if (normalized.includes("finance") || normalized.includes("settlement") || normalized.includes("account")) {
    return "finance-lead";
  }
  if (normalized.includes("delivery") || normalized.includes("dispatch") || normalized.includes("logistics")) {
    return "delivery-lead";
  }
  return "branch-admin";
}

function fromRoleValue(roleValue) {
  return (
    PHARMACY_MEMBER_ROLE_OPTIONS.find((option) => option.value === roleValue)?.label ||
    "Branch admin"
  );
}

export function normalizePharmacyStaffMember(row = {}, index = 0) {
  const roleValue = toRoleValue(row.roleValue || row.role);
  const email = pickFirst(row.email).toLowerCase();
  const phone = pickFirst(row.phone, row.mobile);
  const status = normalizeMemberStatus(row, roleValue);

  return {
    id: pickFirst(
      row.id,
      row.staffNumber,
      row.staff_number,
      row.email,
      row.phone,
      `pharmacy-member-${index + 1}`,
    ),
    fullName: pickFirst(
      row.fullName,
      row.full_name,
      row.invited_full_name,
      row.name,
      [pickFirst(row.first_name), pickFirst(row.last_name)].filter(Boolean).join(" "),
    ),
    email,
    phone,
    dob: pickFirst(row.dob, row.date_of_birth),
    branch: pickFirst(row.branch, row.branch_name, row.branchName, row.location),
    staffNumber: pickFirst(row.staffNumber, row.staff_number, row.employee_id, row.payroll_id),
    roleValue,
    role: pickFirst(row.role, fromRoleValue(roleValue)),
    status,
    invitedAt: pickFirst(row.invitedAt, row.invited_at),
    acceptedAt: pickFirst(row.acceptedAt, row.accepted_at, row.joinedAt, row.joined_at),
    userId: pickFirst(row.userId, row.user_id, row.linkedUserId, row.linked_user_id),
    inviteLink: pickFirst(row.inviteLink, row.invite_link),
    source: pickFirst(row.source, "staff"),
  };
}

function isSyntheticPharmacyMemberId(value = "") {
  const id = String(value || "").trim();
  return !id || id === "principal-owner" || id.startsWith("pharmacy-member-");
}

function getPharmacyMemberIdentity(member = {}) {
  const normalized = normalizePharmacyStaffMember(member, 0);
  return {
    ...normalized,
    emailKey: normalizeEmail(normalized.email),
    phoneKey: normalizePhoneForCompare(normalized.phone),
    nameKey: normalizeText(normalized.fullName),
    branchKey: normalizeText(normalized.branch),
    userKey: String(normalized.userId || "").trim(),
    persistedId: isSyntheticPharmacyMemberId(normalized.id) ? "" : String(normalized.id || "").trim(),
  };
}

function pharmacyMembersMatch(left = {}, right = {}) {
  const a = getPharmacyMemberIdentity(left);
  const b = getPharmacyMemberIdentity(right);

  if (a.persistedId && b.persistedId && a.persistedId === b.persistedId) return true;
  if (a.userKey && b.userKey && a.userKey === b.userKey) return true;
  if (a.emailKey && b.emailKey && a.emailKey === b.emailKey) return true;

  if (a.phoneKey && b.phoneKey && a.phoneKey === b.phoneKey) {
    if (!a.nameKey || !b.nameKey || a.nameKey === b.nameKey) return true;
  }

  return !!(
    a.nameKey &&
    b.nameKey &&
    a.nameKey === b.nameKey &&
    a.branchKey &&
    b.branchKey &&
    a.branchKey === b.branchKey
  );
}

function mergePharmacyMembers(current = {}, incoming = {}) {
  const existing = getPharmacyMemberIdentity(current);
  const next = getPharmacyMemberIdentity(incoming);
  const status =
    next.status === "active" || existing.status === "active" ? "active" : next.status;
  const preferredId = next.persistedId || existing.persistedId || next.id || existing.id;

  return {
    ...current,
    ...incoming,
    id: preferredId,
    fullName: pickFirst(next.fullName, existing.fullName),
    email: pickFirst(next.email, existing.email),
    phone: pickFirst(next.phone, existing.phone),
    dob: pickFirst(next.dob, existing.dob),
    branch: pickFirst(next.branch, existing.branch),
    staffNumber: pickFirst(next.staffNumber, existing.staffNumber),
    roleValue: pickFirst(next.roleValue, existing.roleValue, "branch-admin"),
    role: pickFirst(next.role, existing.role),
    status,
    invitedAt: pickFirst(existing.invitedAt, next.invitedAt),
    acceptedAt:
      status === "active"
        ? pickFirst(next.acceptedAt, existing.acceptedAt, new Date().toISOString())
        : "",
    userId: pickFirst(next.userId, existing.userId),
    inviteLink: pickFirst(next.inviteLink, existing.inviteLink),
    source:
      status === "active"
        ? pickFirst(next.source, existing.source, "invite-accepted")
        : pickFirst(next.source, existing.source),
  };
}

function parseCsvLine(line = "") {
  const cells = [];
  let current = "";
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const next = line[index + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      cells.push(current.trim());
      current = "";
      continue;
    }

    current += char;
  }

  cells.push(current.trim());
  return cells;
}

function normalizeHeader(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export async function parsePharmacyStaffCsvFile(file) {
  const text = await file.text();
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length <= 1) return [];

  const headers = parseCsvLine(lines[0]).map(normalizeHeader);
  return lines
    .slice(1)
    .map((line, index) => {
      const cells = parseCsvLine(line);
      const row = headers.reduce((acc, header, cellIndex) => {
        acc[header] = String(cells[cellIndex] || "").trim();
        return acc;
      }, {});

      return normalizePharmacyStaffMember(row, index);
    })
    .filter((row) => row.fullName || row.email || row.phone);
}

function createOwnerMember(profile = {}) {
  const safeProfile = asRecord(profile);
  const ownerName = pickFirst(safeProfile.adminName);
  const ownerEmail = pickFirst(safeProfile.adminEmail).toLowerCase();
  const ownerPhone = [pickFirst(safeProfile.adminPhoneCc), pickFirst(safeProfile.adminPhone)]
    .filter(Boolean)
    .join("");

  if (!ownerName && !ownerEmail && !ownerPhone) return null;

  return normalizePharmacyStaffMember(
    {
      id: "principal-owner",
      fullName: ownerName,
      email: ownerEmail,
      phone: ownerPhone,
      branch: pickFirst(safeProfile.pharmacyName, safeProfile.tradingName),
      roleValue: "owner",
      role: "Owner",
      source: "owner",
    },
    0,
  );
}

export function buildPharmacyMemberRecords(profile = {}) {
  const safeProfile = asRecord(profile);
  const rows = [];

  const pushUnique = (member) => {
    if (!member) return;
    const normalized = normalizePharmacyStaffMember(member, rows.length);
    if (!normalized.fullName && !normalized.email && !normalized.phone) return;
    if (normalized.status === "revoked") {
      const existingIndex = rows.findIndex((row) => pharmacyMembersMatch(row, normalized));
      if (existingIndex >= 0) {
        rows.splice(existingIndex, 1);
      }
      return;
    }
    const existingIndex = rows.findIndex((row) => pharmacyMembersMatch(row, normalized));
    if (existingIndex >= 0) {
      rows[existingIndex] = mergePharmacyMembers(rows[existingIndex], normalized);
      return;
    }
    rows.push(normalized);
  };

  pushUnique(createOwnerMember(safeProfile));
  (Array.isArray(safeProfile.staffMembers) ? safeProfile.staffMembers : []).forEach((member) =>
    pushUnique(member),
  );

  return rows.sort((left, right) => {
    const rank = (member) => {
      if (member.roleValue === "owner") return 0;
      if (member.status === "active") return 1;
      return 2;
    };
    const rankDiff = rank(left) - rank(right);
    if (rankDiff !== 0) return rankDiff;
    return String(left.fullName || left.email || "").localeCompare(
      String(right.fullName || right.email || ""),
    );
  });
}

export function upsertPharmacyStaffMember(profile = {}, member = {}) {
  const normalized = normalizePharmacyStaffMember(member, 0);
  if (!normalized.fullName && !normalized.email && !normalized.phone) {
    return profile;
  }
  const current = Array.isArray(profile?.staffMembers) ? profile.staffMembers : [];
  const nextMembers = [...current];
  const existingIndex = nextMembers.findIndex((row) => pharmacyMembersMatch(row, normalized));

  if (existingIndex >= 0) {
    nextMembers[existingIndex] = mergePharmacyMembers(nextMembers[existingIndex], normalized);
  } else {
    nextMembers.push(normalized);
  }

  const invitedEmails = nextMembers
    .map((row) => pickFirst(row.email).toLowerCase())
    .filter(Boolean)
    .join("\n");

  return {
    ...profile,
    staffMembers: nextMembers,
    invitedUsers: invitedEmails || profile?.invitedUsers || "",
  };
}

export function invitePharmacyStaffMember(profile = {}, member = {}) {
  return upsertPharmacyStaffMember(profile, {
    ...member,
    status: "invited",
    invitedAt: member?.invitedAt || new Date().toISOString(),
    source: pickFirst(member?.source, "invite"),
  });
}

export function createPharmacyInviteDraft(member = {}) {
  return normalizePharmacyStaffMember(
    {
      ...member,
      status: "invited",
      invitedAt: member?.invitedAt || new Date().toISOString(),
      source: pickFirst(member?.source, "invite"),
    },
    0,
  );
}

export function acceptPharmacyStaffInvite(profile = {}, { email = "", userId = "" } = {}) {
  const cleanEmail = String(email || "").trim().toLowerCase();
  const cleanUserId = String(userId || "").trim();
  if (!cleanEmail) return profile;

  const current = Array.isArray(profile?.staffMembers) ? profile.staffMembers : [];
  let didChange = false;

  const nextMembers = current.map((member) => {
    const normalized = normalizePharmacyStaffMember(member, 0);
    if (normalized.roleValue === "owner") return member;
    if (normalized.email !== cleanEmail) return member;
    if (normalized.status === "active" && normalized.userId === cleanUserId) return member;

    didChange = true;
    return {
      ...member,
      email: cleanEmail,
      status: "active",
      userId: cleanUserId || member?.userId || "",
      acceptedAt: new Date().toISOString(),
      source: "invite-accepted",
    };
  });

  if (!didChange) return profile;

  return {
    ...profile,
    staffMembers: nextMembers,
    invitedUsers: nextMembers
      .map((row) => normalizePharmacyStaffMember(row, 0))
      .filter((row) => row.status === "invited")
      .map((row) => row.email)
      .filter(Boolean)
      .join("\n"),
  };
}

export function removePharmacyStaffMember(profile = {}, memberId = "") {
  const normalizedId = String(memberId || "").trim();
  if (!normalizedId) return profile;

  const nextMembers = (Array.isArray(profile?.staffMembers) ? profile.staffMembers : []).filter(
    (member) => String(member?.id || "").trim() !== normalizedId,
  );

  const invitedEmails = nextMembers
    .map((row) => pickFirst(row.email).toLowerCase())
    .filter(Boolean)
    .join("\n");

  return {
    ...profile,
    staffMembers: nextMembers,
    invitedUsers: invitedEmails || "",
  };
}

export async function revokePersistedPharmacyStaffMember({
  memberId = "",
  ownerUserId = "",
} = {}) {
  const cleanMemberId = String(memberId || "").trim();
  const cleanOwnerUserId = String(ownerUserId || "").trim();

  if (!cleanMemberId || !cleanOwnerUserId) {
    return null;
  }

  const { data, error } = await supabase
    .from(PHARMACY_STAFF_MEMBERS_TABLE)
    .update({
      status: "revoked",
      member_user_id: null,
      updated_at: new Date().toISOString(),
    })
    .eq("id", cleanMemberId)
    .eq("owner_user_id", cleanOwnerUserId)
    .select(
      "id,owner_user_id,member_user_id,invited_email,invited_full_name,invited_phone,role,status,branch_name,inviter_name,invite_link,invited_at,accepted_at",
    )
    .maybeSingle();

  if (error) throw error;
  return data ? mapPersistedPharmacyStaffMember(data) : null;
}

export function createPharmacyInviteBrief(profile = {}) {
  const safeProfile = asRecord(profile);
  const branchName = pickFirst(
    safeProfile.pharmacyName,
    safeProfile.tradingName,
    "your pharmacy branch",
  );
  const ownerName = pickFirst(safeProfile.adminName, "the principal owner");
  return [
    `Join ${branchName} on AfriMed™ Pharmacy.`,
    `${ownerName} is setting up the branch team so prescriptions, stock, payouts, and delivery stay aligned.`,
    "Open the invite page, use your invited email, and request your secure sign-in link there.",
    "Open the newest sign-in email on the same browser or device to continue to Pharmacy Dashboard.",
    createPharmacyInviteSignInUrl(""),
  ].join("\n\n");
}

export function createPharmacyInviteSignInUrl(
  email = "",
  nextPath = "/pharmacy/dashboard",
  membershipId = "",
) {
  const base =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "https://staging.afrimed.tech";
  const url = new URL("/pharmacy/invite", base);
  url.searchParams.set("invite", "pharmacy");
  if (normalizeEmail(email)) {
    url.searchParams.set("email", normalizeEmail(email));
  }
  if (String(nextPath || "").trim().startsWith("/")) {
    url.searchParams.set("next", String(nextPath || "").trim());
  }
  if (String(membershipId || "").trim()) {
    url.searchParams.set("member", String(membershipId || "").trim());
  }
  return url.toString();
}

function mapPersistedPharmacyStaffMember(row = {}) {
  return normalizePharmacyStaffMember(
    {
      id: row.id,
      full_name: row.invited_full_name,
      email: row.invited_email,
      phone: row.invited_phone,
      branch_name: row.branch_name,
      role: row.role,
      status: row.status,
      invited_at: row.invited_at,
      accepted_at: row.accepted_at,
      user_id: row.member_user_id,
      invite_link: row.invite_link,
      source: "db",
    },
    0,
  );
}

function toPersistedPharmacyMemberPayload({
  profile = {},
  member = {},
  ownerUserId = "",
  inviteLink = "",
} = {}) {
  const normalized = normalizePharmacyStaffMember(member, 0);
  return {
    owner_user_id: String(ownerUserId || "").trim(),
    member_user_id: String(normalized.userId || "").trim() || null,
    invited_email: normalizeEmail(normalized.email),
    invited_full_name: pickFirst(normalized.fullName) || null,
    invited_phone: pickFirst(normalized.phone) || null,
    role: normalized.roleValue || "branch-admin",
    status: normalized.status === "active" ? "active" : "invited",
    branch_name: pickFirst(profile?.pharmacyName, profile?.tradingName) || null,
    inviter_name: pickFirst(profile?.adminName) || null,
    invite_link: pickFirst(inviteLink, normalized.inviteLink) || null,
    invited_at: pickFirst(normalized.invitedAt) || new Date().toISOString(),
    accepted_at:
      normalized.status === "active"
        ? pickFirst(normalized.acceptedAt) || new Date().toISOString()
        : null,
  };
}

export async function listAccessiblePharmacyStaffInvites() {
  const { data, error } = await withPharmacyTimeout(
    supabase
    .from(PHARMACY_STAFF_MEMBERS_TABLE)
    .select(
      "id,owner_user_id,member_user_id,invited_email,invited_full_name,invited_phone,role,status,branch_name,inviter_name,invite_link,invited_at,accepted_at,created_at",
    )
    .order("created_at", { ascending: false }),
    PHARMACY_ACCESS_TIMEOUT_MS,
    "List pharmacy members",
  );

  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

export async function hasPersistedPharmacyOwnerProfile(userId = "") {
  const cleanUserId = String(userId || "").trim();
  if (!cleanUserId) return false;

  const { data, error } = await withPharmacyTimeout(
    supabase
    .from("pharmacy_connect_profiles")
    .select("user_id")
    .eq("user_id", cleanUserId)
    .maybeSingle(),
    PHARMACY_ACCESS_TIMEOUT_MS,
    "Read pharmacy owner profile",
  );

  if (error) throw error;
  return !!String(data?.user_id || "").trim();
}

export function hasRemotePharmacyAccess(rows = [], { userId = "", email = "" } = {}) {
  const cleanUserId = String(userId || "").trim();
  const cleanEmail = normalizeEmail(email);

  return (Array.isArray(rows) ? rows : []).some((row) => {
    const ownerUserId = String(row?.owner_user_id || "").trim();
    const memberUserId = String(row?.member_user_id || "").trim();
    const invitedEmail = normalizeEmail(row?.invited_email);
    const status = String(row?.status || "").trim().toLowerCase();

    if (ownerUserId && cleanUserId && ownerUserId === cleanUserId) return true;
    if (status !== "active") return false;

    return (
      (memberUserId && cleanUserId && memberUserId === cleanUserId) ||
      (invitedEmail && cleanEmail && invitedEmail === cleanEmail)
    );
  });
}

export function getActivePharmacyMemberAccess(accessOrRows = [], { userId = "", email = "" } = {}) {
  const rows = Array.isArray(accessOrRows) ? accessOrRows : accessOrRows?.rows;
  const cleanUserId = String(userId || "").trim();
  const cleanEmail = normalizeEmail(email);

  return (
    (Array.isArray(rows) ? rows : []).find((row) => {
      const ownerUserId = String(row?.owner_user_id || "").trim();
      const memberUserId = String(row?.member_user_id || "").trim();
      const invitedEmail = normalizeEmail(row?.invited_email);
      const status = String(row?.status || "").trim().toLowerCase();

      if (status !== "active") return false;
      if (!ownerUserId) return false;
      if (cleanUserId && ownerUserId === cleanUserId) return false;

      return (
        (memberUserId && cleanUserId && memberUserId === cleanUserId) ||
        (invitedEmail && cleanEmail && invitedEmail === cleanEmail)
      );
    }) || null
  );
}

export function hasActivePharmacyMemberAccess(accessOrRows = [], options = {}) {
  return !!getActivePharmacyMemberAccess(accessOrRows, options);
}

export async function resolvePersistedPharmacyAccess({ userId = "", email = "" } = {}) {
  const cleanUserId = String(userId || "").trim();
  const cleanEmail = normalizeEmail(email);

  if (!cleanUserId && !cleanEmail) {
    return {
      allowed: false,
      rows: [],
      hasOwnerProfile: false,
    };
  }

  const cached = readCachedPharmacyAccess({ userId: cleanUserId, email: cleanEmail });
  if (cached?.allowed) {
    return cached;
  }

  try {
    const [ownerRows, memberRows, emailRows, hasOwnerProfile] = await Promise.all([
      cleanUserId
        ? withPharmacyTimeout(
            supabase
              .from(PHARMACY_STAFF_MEMBERS_TABLE)
              .select(
                "id,owner_user_id,member_user_id,invited_email,invited_full_name,invited_phone,role,status,branch_name,inviter_name,invite_link,invited_at,accepted_at,created_at",
              )
              .eq("owner_user_id", cleanUserId)
              .order("created_at", { ascending: false }),
            PHARMACY_ACCESS_TIMEOUT_MS,
            "Read pharmacy owner memberships",
          ).then(({ data, error }) => {
            if (error) throw error;
            return Array.isArray(data) ? data : [];
          })
        : [],
      cleanUserId
        ? withPharmacyTimeout(
            supabase
              .from(PHARMACY_STAFF_MEMBERS_TABLE)
              .select(
                "id,owner_user_id,member_user_id,invited_email,invited_full_name,invited_phone,role,status,branch_name,inviter_name,invite_link,invited_at,accepted_at,created_at",
              )
              .eq("member_user_id", cleanUserId)
              .order("created_at", { ascending: false }),
            PHARMACY_ACCESS_TIMEOUT_MS,
            "Read pharmacy member memberships",
          ).then(({ data, error }) => {
            if (error) throw error;
            return Array.isArray(data) ? data : [];
          })
        : [],
      cleanEmail
        ? withPharmacyTimeout(
            supabase
              .from(PHARMACY_STAFF_MEMBERS_TABLE)
              .select(
                "id,owner_user_id,member_user_id,invited_email,invited_full_name,invited_phone,role,status,branch_name,inviter_name,invite_link,invited_at,accepted_at,created_at",
              )
              .eq("invited_email", cleanEmail)
              .order("created_at", { ascending: false }),
            PHARMACY_ACCESS_TIMEOUT_MS,
            "Read pharmacy email memberships",
          ).then(({ data, error }) => {
            if (error) throw error;
            return Array.isArray(data) ? data : [];
          })
        : [],
      cleanUserId ? hasPersistedPharmacyOwnerProfile(cleanUserId).catch(() => false) : false,
    ]);

    const dedupedRows = [];
    const seenIds = new Set();
    for (const row of [...ownerRows, ...memberRows, ...emailRows]) {
      const rowId = String(row?.id || "").trim();
      if (rowId && seenIds.has(rowId)) continue;
      if (rowId) seenIds.add(rowId);
      dedupedRows.push(row);
    }

    const result = {
      allowed:
        !!hasOwnerProfile ||
        hasRemotePharmacyAccess(dedupedRows, { userId: cleanUserId, email: cleanEmail }),
      rows: dedupedRows,
      hasOwnerProfile: !!hasOwnerProfile,
    };

    writeCachedPharmacyAccess({
      userId: cleanUserId,
      email: cleanEmail,
      value: result,
    });

    return result;
  } catch (error) {
    if (cached) return cached;
    throw error;
  }
}

export async function canUseStagingPharmacyOwnerOnboarding(email = "") {
  const cleanEmail = normalizeStagingEmail(email);
  if (!cleanEmail) return false;

  const stagingLikeRuntime =
    isStagingRuntimeHost(getRuntimeHostname()) || import.meta.env.DEV === true;
  if (!stagingLikeRuntime) return false;

  const cachedAccess = readCachedStagingAccess();
  if (cachedAccess?.email === cleanEmail) return true;

  try {
    const access = await checkStagingAccess(cleanEmail);
    return access?.allowed === true;
  } catch {
    return false;
  }
}

export async function resolvePharmacySettlementOwnerUserId({ userId = "", email = "" } = {}) {
  const cleanUserId = String(userId || "").trim();
  const cleanEmail = normalizeEmail(email);

  if (cleanUserId) {
    const ownsProfile = await hasPersistedPharmacyOwnerProfile(cleanUserId).catch(() => false);
    if (ownsProfile) return cleanUserId;
  }

  const rows = await listAccessiblePharmacyStaffInvites().catch(() => []);
  const matchedRow = (Array.isArray(rows) ? rows : []).find((row) => {
    const status = String(row?.status || "").trim().toLowerCase();
    const memberUserId = String(row?.member_user_id || "").trim();
    const invitedEmail = normalizeEmail(row?.invited_email);
    if (status !== "active") return false;
    return (
      (cleanUserId && memberUserId === cleanUserId) ||
      (cleanEmail && invitedEmail === cleanEmail)
    );
  });

  return String(matchedRow?.owner_user_id || "").trim();
}

export async function persistPharmacyStaffInvite({
  profile = {},
  member = {},
  ownerUserId = "",
} = {}) {
  const cleanOwnerUserId = String(ownerUserId || "").trim();
  const normalized = normalizePharmacyStaffMember(member, 0);
  const normalizedEmail = normalizeEmail(normalized.email);

  if (!normalizedEmail) {
    throw new Error("Invite email is required before AfriMed can save this pharmacy member.");
  }

  if (!cleanOwnerUserId) {
    return normalized;
  }

  const inviteLink = createPharmacyInviteSignInUrl(
    normalized.email,
    "/pharmacy/dashboard",
  );

  const basePayload = toPersistedPharmacyMemberPayload({
    profile,
    member: {
      ...normalized,
      inviteLink,
    },
    ownerUserId: cleanOwnerUserId,
  });

  const { data: row, error } = await supabase
    .from(PHARMACY_STAFF_MEMBERS_TABLE)
    .upsert(basePayload, {
      onConflict: "owner_user_id,invited_email",
    })
    .select(
      "id,owner_user_id,member_user_id,invited_email,invited_full_name,invited_phone,role,status,branch_name,inviter_name,invite_link,invited_at,accepted_at",
    )
    .single();

  if (error) throw error;

  return {
    ...mapPersistedPharmacyStaffMember(row),
    inviteLink: pickFirst(row?.invite_link, inviteLink),
  };
}

export async function acceptPersistedPharmacyStaffInvite({
  membershipId = "",
  email = "",
  userId = "",
} = {}) {
  const cleanMembershipId = String(membershipId || "").trim();
  const cleanEmail = normalizeEmail(email);
  const cleanUserId = String(userId || "").trim();

  if (!cleanUserId || (!cleanMembershipId && !cleanEmail)) {
    return [];
  }

  let query = supabase
    .from(PHARMACY_STAFF_MEMBERS_TABLE)
    .update({
      member_user_id: cleanUserId,
      status: "active",
      accepted_at: new Date().toISOString(),
    })
    .select(
      "id,owner_user_id,member_user_id,invited_email,invited_full_name,invited_phone,role,status,branch_name,inviter_name,invite_link,invited_at,accepted_at",
    )
    .eq("status", "invited");

  if (cleanMembershipId) {
    query = query.eq("id", cleanMembershipId);
  } else {
    query = query.eq("invited_email", cleanEmail);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (Array.isArray(data) ? data : []).map(mapPersistedPharmacyStaffMember);
}

export function mergePersistedPharmacyMembers(profile = {}, persistedRows = []) {
  return (Array.isArray(persistedRows) ? persistedRows : []).reduce((current, row) => {
    const normalized =
      row?.id && row?.invited_email ? mapPersistedPharmacyStaffMember(row) : normalizePharmacyStaffMember(row, 0);
    if (normalized.status === "revoked") {
      return removePharmacyStaffMember(current, normalized.id);
    }
    return upsertPharmacyStaffMember(current, normalized);
  }, profile || {});
}

export function hasLocalPharmacyAccess(profile = {}, { email = "", userId = "" } = {}) {
  const safeProfile = asRecord(profile);
  const cleanEmail = normalizeEmail(email);
  const cleanUserId = String(userId || "").trim();

  const ownerEmail = normalizeEmail(safeProfile?.adminEmail);
  if (ownerEmail && cleanEmail && ownerEmail === cleanEmail) return true;

  const members = buildPharmacyMemberRecords(safeProfile);
  return members.some((member) => {
    if (member.roleValue === "owner") {
      return normalizeEmail(member.email) === cleanEmail;
    }

    return (
      member.status === "active" &&
      ((cleanUserId && String(member.userId || "").trim() === cleanUserId) ||
        (cleanEmail && normalizeEmail(member.email) === cleanEmail))
    );
  });
}

export async function sendPharmacyInviteEmail({
  email = "",
  fullName = "",
  roleValue = "",
  pharmacyName = "",
  inviterName = "",
  inviteLink = "",
  pharmacyLogoUrl = "",
  pharmacyAddressLine1 = "",
  pharmacyAddressLine2 = "",
  pharmacyCityRegionPostal = "",
  pharmacyCountry = "",
} = {}) {
  const targetEmail = normalizeEmail(email);
  if (!targetEmail) {
    throw new Error("Invite email is required.");
  }

  const roleLabel =
    PHARMACY_MEMBER_ROLE_OPTIONS.find((option) => option.value === roleValue)?.label ||
    "Branch admin";
  const accessToken = await resolveFunctionAccessToken({
    refreshIfMissing: false,
    forceRefresh: false,
  });

  if (!String(accessToken || "").trim()) {
    throw new Error("Session expired. Please sign in again.");
  }

  return callFunctionRaw(
    "send-pharmacy-invite",
    {
      email: targetEmail,
      full_name: String(fullName || "").trim(),
      role_label: roleLabel,
      pharmacy_name: String(pharmacyName || "").trim(),
      inviter_name: String(inviterName || "").trim(),
      invite_link: String(inviteLink || createPharmacyInviteSignInUrl(targetEmail)).trim(),
      pharmacy_logo_url: String(pharmacyLogoUrl || "").trim(),
      pharmacy_address_line_1: String(pharmacyAddressLine1 || "").trim(),
      pharmacy_address_line_2: String(pharmacyAddressLine2 || "").trim(),
      pharmacy_city_region_postal: String(pharmacyCityRegionPostal || "").trim(),
      pharmacy_country: String(pharmacyCountry || "").trim(),
    },
    {
      accessToken,
      timeoutMs: 10000,
      preferFreshSession: false,
    },
  );
}

function formatDoctorQueueLabel(...values) {
  for (const value of values) {
    const text = String(value || "").trim();
    if (!text) continue;
    const normalized = text.replace(/^dr\.?\s+/i, "").trim();
    if (!normalized) continue;
    const parts = normalized.split(/\s+/).filter(Boolean);
    const surname = parts[parts.length - 1] || normalized;
    return `Dr ${surname}`;
  }
  return "Doctor";
}

function countPrescriptionItems(meta = {}) {
  const candidates = [
    meta?.items,
    meta?.prescriptionItems,
    meta?.medications,
    meta?.medicationItems,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate.reduce((total, item) => {
        const quantity = parseNumber(
          item?.quantity ?? item?.qty ?? item?.count ?? item?.units ?? 1,
        );
        return total + Math.max(quantity || 1, 1);
      }, 0);
    }
  }

  const direct = parseNumber(meta?.itemsCount ?? meta?.itemCount ?? meta?.lineItemsCount);
  return direct > 0 ? direct : 1;
}

function readMinorInt(...values) {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return Math.round(parsed);
  }
  return 0;
}

function isSettledPharmacyPaymentStatus(status = "") {
  return ["paid", "succeeded", "completed"].includes(String(status || "").trim().toLowerCase());
}

function pickImage(index, row = {}) {
  return (
    pickFirst(row.image, row.imageUrl, row.image_url, row.photoUrl, row.pictureUrl) ||
    PHARMACY_STOCK_PLACEHOLDER ||
    IMAGE_POOL[index % IMAGE_POOL.length]
  );
}

function normalizeInventoryRow(row = {}, index = 0) {
  const item = pickFirst(row.item, row.itemName, row.medicine_name, row.product_name);
  const detail = pickFirst(row.detail, row.strength, row.pack_size);
  const stockId = pickFirst(row.stockId, row.stock_id, row.sku, row.item_code, `INV-${index + 1}`);
  const currentStock = parseNumber(row.currentStock ?? row.current_stock ?? row.quantity_on_hand);
  const minimalStock = parseNumber(row.minimalStock ?? row.minimal_stock ?? row.reorder_level);
  const price = normalizeInventoryPrice(row, row.currency || "ZAR");
  const unitPriceValue = Number(price.amount || 0);
  const explicitImageUrl = pickFirst(
    row.imageUrl,
    row.image_url,
    row.photoUrl,
    row.pictureUrl,
    row.image,
  );

  return {
    id: pickFirst(row.id, stockId, `${item || "item"}-${index + 1}`),
    item: item || "Medicine",
    detail,
    category: pickFirst(row.category, row.classification),
    stockId,
    currentStock,
    minimalStock,
    unitPriceValue,
    unitPrice: unitPriceValue ? unitPriceValue.toFixed(2) : pickFirst(row.unitPrice, row.unit_price, row.retail_price, row.price),
    currency: price.currency,
    supplier: pickFirst(row.supplier, row.vendor, row.supplier_name),
    branch: pickFirst(row.branch, row.branch_name, row.location),
    batchNumber: pickFirst(row.batchNumber, row.batch_number),
    expiryDate: pickFirst(row.expiryDate, row.expiry_date),
    binLocation: pickFirst(row.binLocation, row.bin_location),
    imageUrl: explicitImageUrl,
    image: explicitImageUrl || pickImage(index, row),
    status: pickFirst(row.status, "active"),
  };
}

function isLowStock(row) {
  return Number(row.currentStock || 0) <= Number(row.minimalStock || 0);
}

function isColdChain(row) {
  const text = normalizeText(
    [row.item, row.detail, row.category, row.supplier].filter(Boolean).join(" "),
  );
  return /vaccine|insulin|cold chain|biologic|refriger/i.test(text);
}

function resolvePharmacyCurrency({ explicitCurrency = "", profile = null, meta = null } = {}) {
  return resolveBranchCurrency({
    explicitCurrency,
    profile,
    meta,
    fallbackCurrency: "ZAR",
  });
}

function sortByShortage(rows = []) {
  return [...rows].sort((a, b) => {
    const shortageA = Number(a.minimalStock || 0) - Number(a.currentStock || 0);
    const shortageB = Number(b.minimalStock || 0) - Number(b.currentStock || 0);
    return shortageB - shortageA;
  });
}

export function readPharmacyProfile() {
  return readJson(PHARMACY_PROFILE_STORAGE_KEY, null);
}

export function readPharmacyProfileForActor({ userId = "", email = "" } = {}) {
  const profile = readPharmacyProfile();
  return hasLocalPharmacyAccess(profile, { userId, email }) ? profile : null;
}

export function readPharmacyNotificationsForActor(actor = {}) {
  return readPharmacyProfileForActor(actor) ? readPharmacyNotifications() : [];
}

export function isPharmacyConnectOwnershipReady(status = {}) {
  const connected = Boolean(status?.connected);
  const accountId = String(status?.account_id || status?.accountId || "").trim();
  const payoutsEnabled = Boolean(status?.payouts_enabled || status?.payoutsEnabled);
  const detailsSubmitted = Boolean(status?.details_submitted || status?.detailsSubmitted);
  const requiresAction = Boolean(status?.requires_action || status?.requiresAction);
  const capabilities =
    status?.capabilities && typeof status.capabilities === "object" ? status.capabilities : {};
  const transfersCapability = String(
    capabilities?.transfers || capabilities?.legacy_payments || "",
  )
    .trim()
    .toLowerCase();
  const transfersReady =
    transfersCapability === "active" ||
    payoutsEnabled;

  return connected && !!accountId && detailsSubmitted && !requiresAction && transfersReady;
}

export function derivePharmacyOnboardingTruth(profile = {}, status = {}) {
  const accountId = String(
    status?.account_id || status?.accountId || profile?.stripeAccountId || "",
  ).trim();
  const ownershipReady = isPharmacyConnectOwnershipReady({
    ...status,
    account_id: accountId,
  });
  const onboardingState = ownershipReady ? "onboarded" : "onboarding_incomplete";
  const paymentState = ownershipReady ? "payments_ready" : "payments_blocked";

  return {
    accountId,
    ownershipReady,
    onboardingState,
    paymentState,
    stripeConnected: Boolean(status?.connected || profile?.stripeConnected),
    stripePayoutsEnabled: Boolean(status?.payouts_enabled || status?.payoutsEnabled),
    stripeDetailsSubmitted: Boolean(status?.details_submitted || status?.detailsSubmitted),
    stripeRequiresAction: Boolean(status?.requires_action || status?.requiresAction),
  };
}

export function savePharmacyProfile(profile) {
  writeJson(PHARMACY_PROFILE_STORAGE_KEY, profile || {});
  window.dispatchEvent(new Event(PHARMACY_PROFILE_CHANGED_EVENT));
}

export function savePharmacyOnboardingTruth(profile = {}, status = {}) {
  const truth = derivePharmacyOnboardingTruth(profile, status);
  const nextProfile = {
    ...(profile || {}),
    stripeAccountId: truth.accountId || String(profile?.stripeAccountId || "").trim(),
    stripeConnected: truth.stripeConnected,
    stripePayoutsEnabled: truth.stripePayoutsEnabled,
    stripeDetailsSubmitted: truth.stripeDetailsSubmitted,
    stripeRequiresAction: truth.stripeRequiresAction,
    onboardingState: truth.onboardingState,
    paymentState: truth.paymentState,
  };

  savePharmacyProfile(nextProfile);

  try {
    localStorage.setItem(PHARMACY_ONBOARDED_STORAGE_KEY, truth.ownershipReady ? "true" : "false");
  } catch {
    // ignore storage failures
  }

  return nextProfile;
}

export function upsertPharmacyInventoryItem(profile = {}, item = {}) {
  const normalized = normalizeInventoryRow(item, 0);
  const itemId = pickFirst(normalized.id, normalized.stockId, normalized.item);

  if (!itemId) return profile;

  const currentItems = Array.isArray(profile?.inventoryItems) ? profile.inventoryItems : [];
  const nextItems = [...currentItems];
  const existingIndex = nextItems.findIndex((row) => {
    const normalizedRow = normalizeInventoryRow(row, 0);
    return pickFirst(normalizedRow.id, normalizedRow.stockId, normalizedRow.item) === itemId;
  });

  if (existingIndex >= 0) {
    nextItems.splice(existingIndex, 1, normalized);
  } else {
    nextItems.unshift(normalized);
  }

  return {
    ...profile,
    inventoryItems: nextItems,
    inventoryCsvNames:
      Array.isArray(profile?.inventoryCsvNames) && profile.inventoryCsvNames.length
        ? profile.inventoryCsvNames
        : [],
  };
}

export function readPharmacyNotifications() {
  return readJson(PHARMACY_NOTIFICATIONS_STORAGE_KEY, []);
}

export function markPharmacyNotificationsRead(ids = []) {
  const idSet = new Set((Array.isArray(ids) ? ids : [ids]).map((value) => String(value || "").trim()).filter(Boolean));
  if (!idSet.size) return readPharmacyNotifications();
  const next = readPharmacyNotifications().map((item) =>
    idSet.has(String(item.id || "").trim())
      ? { ...item, read: true, readAt: new Date().toISOString() }
      : item,
  );
  writeJson(PHARMACY_NOTIFICATIONS_STORAGE_KEY, next);
  window.dispatchEvent(new Event(PHARMACY_NOTIFICATIONS_CHANGED_EVENT));
  return next;
}

export function getUnreadPharmacyNotificationCount(items = []) {
  return (Array.isArray(items) ? items : []).filter((item) => !item?.read).length;
}

export function buildInventorySnapshot(
  profile,
  { inventoryRowsOverride = null, fallbackInventoryRows = [], fallbackFeaturedItems = [] } = {},
) {
  const inventoryCurrency = resolvePharmacyCurrency({
    explicitCurrency: profile?.currency,
    profile,
  });
  const imported = Array.isArray(inventoryRowsOverride)
    ? inventoryRowsOverride
    : Array.isArray(profile?.inventoryItems)
      ? profile.inventoryItems
      : [];
  const hasImportedInventory = imported.length > 0;
  const baseRows = hasImportedInventory ? imported : fallbackInventoryRows;
  const inventoryRows = baseRows.map((row, index) => normalizeInventoryRow(row, index));
  const lowStockRows = sortByShortage(inventoryRows.filter(isLowStock));
  const featuredRows = hasImportedInventory
    ? [...inventoryRows]
        .sort((a, b) => Number(b.unitPriceValue || 0) - Number(a.unitPriceValue || 0))
        .slice(0, 5)
        .map((row) => ({
          name: row.item,
          detail: row.detail,
          maker: row.supplier || row.category || row.branch || "",
          amount: formatCurrency(row.unitPriceValue || row.unitPrice || 0, row.currency || inventoryCurrency),
          image: row.image,
        }))
    : fallbackFeaturedItems;

  return {
    hasImportedInventory,
    inventoryRows,
    lowStockRows,
    featuredRows,
    metrics: {
      trackedSkus: inventoryRows.length,
      lowStockAlerts: lowStockRows.length,
      coldChainItems: inventoryRows.filter(isColdChain).length,
      availableRows: inventoryRows.filter((row) => Number(row.currentStock || 0) > 0).length,
    },
  };
}

function buildLowStockNotifications(rows = [], existing = new Map()) {
  return sortByShortage((Array.isArray(rows) ? rows : []).filter(isLowStock))
    .slice(0, 12)
    .map((row) => {
      const id = `pharmacy-low-stock:${String(row.stockId || row.id || "").trim()}`;
      const message = `${row.item} is at ${row.currentStock}. Reorder level is ${row.minimalStock}.`;
      const prior = existing.get(id);
      const read = Boolean(prior?.read) && String(prior?.message || "").trim() === message;
      return {
        id,
        kind: "low-stock",
        title: "Low stock alert",
        message,
        priority: "important",
        source: "pharmacy",
        audience: "pharmacy",
        channels: {
          in_app: true,
          push: false,
          sound: false,
          vibration: false,
        },
        quiet_by_default: true,
        escalation_eligible: true,
        group_key: "pharmacy:low-stock",
        group_title: "Low-stock items",
        group_message: "{count} stock lines need reorder attention.",
        read,
        createdAt: read && prior?.createdAt ? prior.createdAt : new Date().toISOString(),
        route: "/pharmacy/inventory",
        stockId: row.stockId,
        item: row.item,
      };
    });
}

function extractMedicationName(meta = {}) {
  return pickFirst(
    meta.medicineName,
    meta.medicationName,
    meta.medicine,
    meta.medication,
    meta.composition,
    meta.ingredients,
  );
}

function derivePrescriptionMode(profile, index) {
  const pickup = Boolean(profile?.pickupEnabled);
  const delivery = Boolean(profile?.deliveryEnabled);
  if (pickup && delivery) return index % 2 === 0 ? "Delivery" : "Pickup";
  if (delivery) return "Delivery";
  return "Pickup";
}

function createQueueRowFromPrescription(
  row = {},
  profile = {},
  inventoryRows = [],
  patientContextByUserId = new Map(),
  doctorContextByUserId = new Map(),
  appointmentIdentityById = new Map(),
  index = 0,
) {
  const meta = row.meta || {};
  const currency = resolvePharmacyCurrency({
    explicitCurrency: meta?.currency,
    profile,
    meta,
  });
  const requestedFulfillmentMode = normalizeText(
    meta.requested_fulfillment_mode || meta.fulfillment_mode,
  ).toLowerCase();
  const medicineName = extractMedicationName(meta);
  const medicineNeedle = normalizeText(medicineName);
  const appointmentIdentity =
    appointmentIdentityById.get(String(row.appointment_id || "").trim()) || {};
  const patientContext = resolveQueuePatientContext(patientContextByUserId, row, appointmentIdentity);
  const doctorContext = resolveQueueDoctorContext(doctorContextByUserId, row);
  const careSubjectType = pickFirst(
    meta.careSubjectType,
    appointmentIdentity.careSubjectType,
    "self",
  ).toLowerCase();
  const inventoryMatch = inventoryRows.find((item) => {
    if (!medicineNeedle) return false;
    const hay = normalizeText(`${item.item} ${item.detail}`);
    return hay.includes(medicineNeedle) || medicineNeedle.includes(normalizeText(item.item));
  });
  const subtotalValue = Number(inventoryMatch?.unitPriceValue || 0);
  const deliveryFee = profile?.deliveryEnabled ? parseNumber(profile?.deliveryBaseFee) : 0;
  const isClaimed = Boolean(normalizeText(meta.pharmacy_owner_user_id));
  const status = !inventoryMatch
    ? "Needs stock attention"
    : Number(inventoryMatch.currentStock || 0) <= Number(inventoryMatch.minimalStock || 0)
      ? "Ready to verify"
      : isClaimed
        ? "Stock confirmed"
        : "Ready to verify";

  return {
    id: prescriptionIdentifier(row),
    prescriptionId: String(row.id || "").trim(),
    patient: pickFirst(
      careSubjectType === "dependant" ? appointmentIdentity.patientName : patientContext.fullName,
      careSubjectType === "dependant" ? meta.patientName : patientContext.displayName,
      meta.patientName,
      "Patient",
    ),
    patientUserId: String(row.patient_user_id || "").trim(),
    patientAvatarUrl:
      careSubjectType === "dependant"
        ? PATIENT_AVATAR_PLACEHOLDER
        : pickResolvedAvatar(
            patientContext.avatarUrl,
            appointmentIdentity.patientAvatar,
            meta.patientAvatarUrl,
            meta.patient_avatar_url,
          ),
    careSubjectType,
    guardianName: pickFirst(meta.guardianName, appointmentIdentity.guardianName),
    responsibleRelationship: pickFirst(
      meta.responsibleRelationship,
      appointmentIdentity.responsibleRelationship,
      meta.care_subject_relationship,
    ),
    prescriberAvatarUrl: pickResolvedAvatar(
      meta.doctorAvatarUrl,
      meta.doctor_avatar_url,
      doctorContext.avatarUrl,
    ),
    doctor: formatDoctorQueueLabel(meta.doctorFullName, meta.doctorName),
    doctorUserId: String(row.doctor_user_id || "").trim(),
    items: medicineName || "Prescription item",
    mode:
      requestedFulfillmentMode === "delivery" || requestedFulfillmentMode === "pickup"
        ? requestedFulfillmentMode === "delivery"
          ? "Delivery"
          : "Pickup"
        : derivePrescriptionMode(profile, index),
    subtotal: formatCurrency(subtotalValue, currency),
    delivery: formatCurrency(deliveryFee, currency),
    status,
    hasInventoryMatch: Boolean(inventoryMatch),
    inventoryMatch,
    issuedAt: row.issued_at || row.created_at || new Date().toISOString(),
    source: "live",
  };
}

function createQueueRowFromFulfillment(
  fulfillment = {},
  prescription = {},
  profile = {},
  inventoryRows = [],
  patientContextByUserId = new Map(),
  doctorContextByUserId = new Map(),
  appointmentIdentityById = new Map(),
  index = 0,
) {
  const meta = prescription?.meta || {};
  const currency = resolvePharmacyCurrency({
    explicitCurrency: fulfillment?.currency || meta?.currency,
    profile,
    meta,
  });
  const fulfillmentStatus = pickFirst(fulfillment?.status, "pharmacy_selected").toLowerCase();
  const requestedFulfillmentMode = normalizeText(
    meta.requested_fulfillment_mode || meta.fulfillment_mode,
  ).toLowerCase();
  const medicineName = extractMedicationName(meta);
  const medicineNeedle = normalizeText(medicineName);
  const appointmentIdentity =
    appointmentIdentityById.get(String(prescription?.appointment_id || "").trim()) || {};
  const patientContext = resolveQueuePatientContext(
    patientContextByUserId,
    prescription,
    appointmentIdentity,
  );
  const doctorContext = resolveQueueDoctorContext(doctorContextByUserId, prescription);
  const careSubjectType = pickFirst(
    meta.careSubjectType,
    appointmentIdentity.careSubjectType,
    "self",
  ).toLowerCase();
  const inventoryMatch = inventoryRows.find((item) => {
    if (!medicineNeedle) return false;
    const hay = normalizeText(`${item.item} ${item.detail}`);
    return hay.includes(medicineNeedle) || medicineNeedle.includes(normalizeText(item.item));
  });
  const quotedPrice = Number(fulfillment?.price || 0);
  const deliveryFeeValue = requestedFulfillmentMode === "delivery"
    ? parseNumber(meta?.delivery_fee_cents != null ? Number(meta.delivery_fee_cents) / 100 : profile?.deliveryBaseFee || 0)
    : 0;
  const quotedMedicineSubtotal = meta?.medicine_subtotal_cents != null
    ? Number(meta.medicine_subtotal_cents) / 100
    : Math.max(quotedPrice - deliveryFeeValue, 0);
  const deliveryQuoteIncomplete = requestedFulfillmentMode === "delivery" &&
    !["paid", "ready_for_pickup"].includes(fulfillmentStatus) && meta.delivery_quote_confirmed !== true;
  const suggestedPrice = Number(inventoryMatch?.unitPriceValue || 0);
  const subtotalValue = ["awaiting_payment", "paid", "ready_for_pickup"].includes(fulfillmentStatus) && quotedPrice > 0
    ? quotedMedicineSubtotal
    : suggestedPrice;

  return {
    id: prescriptionIdentifier(prescription),
    prescriptionId: String(prescription?.id || "").trim(),
    fulfillmentId: String(fulfillment?.id || "").trim(),
    patient: pickFirst(
      careSubjectType === "dependant" ? appointmentIdentity.patientName : patientContext.fullName,
      careSubjectType === "dependant" ? meta.patientName : patientContext.displayName,
      meta.patientName,
      "Patient",
    ),
    patientUserId: String(prescription?.patient_user_id || "").trim(),
    patientAvatarUrl:
      careSubjectType === "dependant"
        ? PATIENT_AVATAR_PLACEHOLDER
        : pickResolvedAvatar(
            patientContext.avatarUrl,
            appointmentIdentity.patientAvatar,
            meta.patientAvatarUrl,
            meta.patient_avatar_url,
          ),
    careSubjectType,
    guardianName: pickFirst(meta.guardianName, appointmentIdentity.guardianName),
    responsibleRelationship: pickFirst(
      meta.responsibleRelationship,
      appointmentIdentity.responsibleRelationship,
      meta.care_subject_relationship,
    ),
    prescriberAvatarUrl: pickResolvedAvatar(
      meta.doctorAvatarUrl,
      meta.doctor_avatar_url,
      doctorContext.avatarUrl,
    ),
    doctor: formatDoctorQueueLabel(meta.doctorFullName, meta.doctorName),
    doctorUserId: String(prescription?.doctor_user_id || "").trim(),
    items: medicineName || "Prescription item",
    mode:
      requestedFulfillmentMode === "delivery" || requestedFulfillmentMode === "pickup"
        ? requestedFulfillmentMode === "delivery"
          ? "Delivery"
          : "Pickup"
        : derivePrescriptionMode(profile, index),
    subtotal: subtotalValue > 0 ? formatCurrency(subtotalValue, currency) : "Pending price",
    delivery: formatCurrency(deliveryFeeValue, currency),
    deliveryFeeValue,
    status: fulfillmentStatus,
    priceValue: quotedMedicineSubtotal,
    deliveryQuoteIncomplete,
    total: quotedPrice > 0 ? formatCurrency(quotedPrice, currency) : "",
    hasInventoryMatch: Boolean(inventoryMatch),
    inventoryMatch,
    issuedAt: prescription?.issued_at || prescription?.created_at || fulfillment?.created_at || new Date().toISOString(),
    updatedAt: fulfillment?.updated_at || fulfillment?.created_at || prescription?.issued_at || prescription?.created_at || "",
    conversationId: String(fulfillment?.conversation_id || "").trim(),
    qrPath: `/pharmacy/orders/${String(prescription?.id || "").trim()}`,
    source: "fulfillment",
  };
}

function prescriptionBelongsToPharmacy(meta = {}, ownerIds = new Set()) {
  const requestedOwnerUserId = normalizeText(
    meta.requested_pharmacy_owner_user_id ||
      meta.pharmacy_requested_owner_user_id ||
      meta.requestedPharmacyOwnerUserId,
  );
  const claimedOwnerUserId = normalizeText(meta.pharmacy_owner_user_id);

  if (claimedOwnerUserId) return ownerIds.has(claimedOwnerUserId);
  if (requestedOwnerUserId) return ownerIds.has(requestedOwnerUserId);
  return false;
}

function resolveQueuePatientContext(patientContextByUserId = new Map(), row = {}, appointmentIdentity = {}) {
  const meta = row?.meta && typeof row.meta === "object" ? row.meta : {};
  const refs = [
    String(row?.patient_user_id || "").trim(),
    String(meta?.patientAuthUserId || "").trim(),
    String(meta?.patientRecordId || "").trim(),
    String(appointmentIdentity?.patientUserId || "").trim(),
  ].filter(Boolean);

  for (const ref of refs) {
    const match = patientContextByUserId.get(ref);
    if (match) return match;
  }

  return {};
}

function resolveQueueDoctorContext(doctorContextByUserId = new Map(), row = {}) {
  const meta = row?.meta && typeof row.meta === "object" ? row.meta : {};
  const refs = [
    String(row?.doctor_user_id || "").trim(),
    String(meta?.doctorAuthUserId || "").trim(),
    String(meta?.doctorProfileId || "").trim(),
  ].filter(Boolean);

  for (const ref of refs) {
    const match = doctorContextByUserId.get(ref);
    if (match) return match;
  }

  return {};
}

async function loadPatientContexts(client, rows = []) {
  const prescriptionIds = Array.from(new Set(
    (Array.isArray(rows) ? rows : []).map((row) => String(row?.id || "").trim()).filter(Boolean),
  ));
  if (!prescriptionIds.length) return new Map();

  // This RPC projects identity only for prescriptions assigned to the signed-in
  // pharmacy. Raw patient_profiles/profile reads are not part of pharmacy scope.
  const map = new Map();
  for (let offset = 0; offset < prescriptionIds.length; offset += 200) {
    const { data, error } = await client.rpc("afrimed_pharmacy_assigned_patient_identity", {
      p_prescription_ids: prescriptionIds.slice(offset, offset + 200),
    });
    if (error) throw error;
    const identities = await Promise.all((Array.isArray(data) ? data : []).map(async (row) => {
      const hydrated = await hydratePatientProfileAvatar({
        profile_photo_path: row?.avatar_path || "",
      }, client);
      return { row, avatarUrl: hydrated?._avatarHydration?.signed ? hydrated.profile_image_url : "" };
    }));
    identities.forEach(({ row, avatarUrl }) => {
      const patientUserId = String(row?.patient_user_id || "").trim();
      const patientRowId = String(row?.patient_row_id || "").trim();
      const prescriptionId = String(row?.prescription_id || "").trim();
      const context = { fullName: pickFirst(row?.patient_name), avatarUrl };
      [patientUserId, patientRowId, prescriptionId].filter(Boolean).forEach((key) => map.set(key, context));
    });
  }
  return map;
}

async function loadDoctorContexts(client, rows = []) {
  const doctorRefs = Array.from(
    new Set(
      (Array.isArray(rows) ? rows : [])
        .flatMap((row) => [
          String(row?.doctor_user_id || "").trim(),
          String(row?.meta?.doctorAuthUserId || "").trim(),
          String(row?.meta?.doctorProfileId || "").trim(),
        ])
        .filter(Boolean),
    ),
  );

  if (!doctorRefs.length) return new Map();

  const { data: doctorRows } = await client
    .from("doctor_profiles")
    .select("id, user_id")
    .in("id", doctorRefs);

  const doctorUserIds = Array.from(
    new Set(
      doctorRefs
        .concat((Array.isArray(doctorRows) ? doctorRows : []).map((row) => String(row?.user_id || "").trim()))
        .filter(Boolean),
    ),
  );

  const [profilesRes, doctorProfilesRes] = await Promise.all([
    client
      .from("profiles")
      .select("user_id, display_name, avatar_url")
      .in("user_id", doctorUserIds),
    client
      .from("doctor_profiles")
      .select(
        "user_id, full_name, first_name, last_name, profile_photo_path, profile_image_url, avatar_url",
      )
      .in("user_id", doctorUserIds),
  ]);

  const map = new Map();

  (Array.isArray(profilesRes?.data) ? profilesRes.data : []).forEach((row) => {
    const userId = String(row?.user_id || "").trim();
    if (!userId) return;
    map.set(userId, {
      displayName: pickFirst(row?.display_name),
      fullName: "",
      avatarUrl: resolveDoctorLikeAvatar(row),
    });
  });

  (Array.isArray(doctorProfilesRes?.data) ? doctorProfilesRes.data : []).forEach((row) => {
    const userId = String(row?.user_id || "").trim();
    if (!userId) return;
    const prior = map.get(userId) || {};
    const fullName = pickFirst(
      row?.full_name,
      [pickFirst(row?.first_name), pickFirst(row?.last_name)].filter(Boolean).join(" "),
    );
    map.set(userId, {
      ...prior,
      fullName: fullName || prior.fullName || "",
      avatarUrl: prior.avatarUrl || resolveDoctorLikeAvatar(row),
    });
  });

  (Array.isArray(doctorRows) ? doctorRows : []).forEach((row) => {
    const recordId = String(row?.id || "").trim();
    const userId = String(row?.user_id || "").trim();
    if (!recordId || !userId || !map.has(userId)) return;
    map.set(recordId, map.get(userId));
  });

  return map;
}

function resolveCompletedPrescriptionStates() {
  return new Set([
    "dispensed",
    "fulfilled",
    "completed",
    "delivered",
    "picked_up",
    "picked-up",
    "picked up",
  ]);
}

function derivePrescriptionAmounts(row = {}, inventoryRows = []) {
  const meta = row.meta || {};
  const itemCount = countPrescriptionItems(meta);
  const subtotal =
    parseNumber(
      meta?.subtotal ??
        meta?.medicineSubtotal ??
        meta?.medicine_subtotal ??
        meta?.amount ??
        meta?.total,
    ) || 0;
  const delivery =
    parseNumber(
      meta?.deliveryFee ??
        meta?.delivery_fee ??
        meta?.deliveryCost ??
        meta?.delivery_cost ??
        meta?.courierFee,
    ) || 0;

  if (subtotal > 0) {
    return {
      itemCount,
      medicineRevenue: subtotal,
      deliveryCollected: delivery,
    };
  }

  const medicineNeedle = normalizeText(extractMedicationName(meta));
  const inventoryMatch = inventoryRows.find((item) => {
    if (!medicineNeedle) return false;
    const hay = normalizeText(`${item.item} ${item.detail}`);
    return hay.includes(medicineNeedle) || medicineNeedle.includes(normalizeText(item.item));
  });
  const matchedUnitPrice = Number(inventoryMatch?.unitPriceValue || 0);

  return {
    itemCount,
    medicineRevenue: matchedUnitPrice > 0 ? matchedUnitPrice * Math.max(itemCount, 1) : 0,
    deliveryCollected: delivery,
  };
}

function normalizeFallbackQueueRow(row = {}, profile = {}, inventoryRows = [], index = 0) {
  const inventoryMatch = inventoryRows[index % Math.max(inventoryRows.length, 1)] || null;
  const currency = resolvePharmacyCurrency({
    explicitCurrency: row?.currency,
    profile,
    meta: row,
  });
  return {
    ...row,
    items: row.items || inventoryMatch?.item || "Prescription item",
    subtotal: row.subtotal || formatCurrency(Number(inventoryMatch?.unitPriceValue || 0), currency),
    delivery:
      row.delivery ||
      formatCurrency(profile?.deliveryEnabled ? parseNumber(profile?.deliveryBaseFee) : 0, currency),
    hasInventoryMatch: Boolean(inventoryMatch),
    inventoryMatch,
    source: "preview",
  };
}

function normalizeMinorCurrency(value = "ZAR") {
  return normalizeCurrencyCode(value, "ZAR");
}

function createMonthlyRevenueSeries(rows = []) {
  const now = new Date();
  const monthBuckets = [];
  const monthIndex = new Map();

  for (let offset = 11; offset >= 0; offset -= 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    const key = `${date.getFullYear()}-${date.getMonth()}`;
    const bucket = {
      key,
      sourceDate: date.toISOString(),
      month: date.toLocaleDateString("en-CH", { month: "short" }),
      total: 0,
    };
    monthIndex.set(key, monthBuckets.length);
    monthBuckets.push(bucket);
  }

  rows.forEach((row) => {
    const sourceDate = new Date(row?.updated_at || row?.created_at || "");
    if (Number.isNaN(sourceDate.getTime())) return;
    const key = `${sourceDate.getFullYear()}-${sourceDate.getMonth()}`;
    const bucketPosition = monthIndex.get(key);
    if (bucketPosition == null) return;

    const meta = row?.meta && typeof row.meta === "object" ? row.meta : {};
    const medicineSubtotalCents = readMinorInt(
      row?.medicine_subtotal_cents,
      meta?.medicine_subtotal_cents,
      meta?.medicine_subtotal,
    );
    monthBuckets[bucketPosition].total += medicineSubtotalCents / 100;
    monthBuckets[bucketPosition].sourceDate = sourceDate.toISOString();
  });

  return monthBuckets.map((bucket, index) => ({
    ...bucket,
    prevTotal: index > 0 ? monthBuckets[index - 1]?.total || 0 : 0,
  }));
}

function createTopMedicineRows(rows = [], inventoryRows = []) {
  const grouped = new Map();
  const catalog = Array.isArray(inventoryRows) ? inventoryRows : [];

  function pickBestInventoryMatch(name = "", detail = "", maker = "") {
    const needles = normalizeText([name, detail, maker].filter(Boolean).join(" "))
      .split(" ")
      .filter((token) => token.length >= 3);
    const exactNeedle = normalizeText([name, detail].filter(Boolean).join(" "));

    let bestMatch = null;
    let bestScore = 0;

    catalog.forEach((item) => {
      const haystack = normalizeText(
        [item?.item, item?.detail, item?.category, item?.supplier].filter(Boolean).join(" "),
      );
      if (!haystack) return;

      let score = 0;
      if (exactNeedle && (haystack.includes(exactNeedle) || exactNeedle.includes(normalizeText(item?.item)))) {
        score += 10;
      }
      needles.forEach((token) => {
        if (haystack.includes(token)) score += 2;
      });

      if (score > bestScore) {
        bestMatch = item;
        bestScore = score;
      }
    });

    return bestScore > 0 ? bestMatch : null;
  }

  rows.forEach((row) => {
    const meta = row?.meta && typeof row.meta === "object" ? row.meta : {};
    const name =
      pickFirst(
        meta?.medicineName,
        meta?.medicine_name,
        meta?.medicationName,
        meta?.medication_name,
        meta?.composition,
        meta?.ingredients,
      ) || "Prescription item";
    const detail = pickFirst(
      meta?.strengthLabel,
      meta?.strength_label,
      meta?.form,
      meta?.dose_form,
      meta?.ingredients,
    );
    const maker = pickFirst(meta?.manufacturer, meta?.maker, meta?.brand_name);
    const itemCount = Math.max(
      readMinorInt(meta?.prescription_item_count, meta?.items_count, meta?.item_count, 1),
      1,
    );
    const revenueCents = readMinorInt(
      row?.medicine_subtotal_cents,
      meta?.medicine_subtotal_cents,
      meta?.medicine_subtotal,
    );
    const key = normalizeText(name).toLowerCase() || String(row?.prescription_id || row?.id || "").trim();
    const inventoryMatch = pickBestInventoryMatch(name, detail, maker);
    const existing = grouped.get(key) || {
      name,
      detail,
      maker,
      soldCount: 0,
      revenueCents: 0,
      image: pickFirst(inventoryMatch?.image, inventoryMatch?.imageUrl),
    };

    existing.soldCount += itemCount;
    existing.revenueCents += revenueCents;
    if (!existing.detail) existing.detail = detail || pickFirst(inventoryMatch?.detail);
    if (!existing.maker) existing.maker = maker || pickFirst(inventoryMatch?.supplier, inventoryMatch?.category);
    if (!existing.image) existing.image = pickFirst(inventoryMatch?.image, inventoryMatch?.imageUrl);
    grouped.set(key, existing);
  });

  return Array.from(grouped.values())
    .sort((left, right) => {
      if (right.revenueCents !== left.revenueCents) return right.revenueCents - left.revenueCents;
      return right.soldCount - left.soldCount;
    })
    .slice(0, 5)
    .map((row, index) => ({
      ...row,
      revenue: row.revenueCents / 100,
      image: row.image || pickImage(index, row),
    }));
}

function normalizePharmacyPaymentStatus(row = {}) {
  const meta = row?.meta && typeof row.meta === "object" ? row.meta : {};
  const rawStatus = String(row?.status || "").trim().toLowerCase() || "pending";
  const refundStatus = String(meta?.stripe_refund_status || "").trim().toLowerCase();

  if (refundStatus === "pending") return "refund_pending";
  if (refundStatus === "succeeded") return "refunded";
  return rawStatus;
}

function pharmacyPaymentCanonicalKey(row = {}) {
  const meta = row?.meta && typeof row.meta === "object" ? row.meta : {};
  const prescriptionId = String(row?.prescription_id || meta?.prescription_id || "").trim();
  if (prescriptionId) return `prescription:${prescriptionId}`;

  const paymentIntentId = String(
    row?.stripe_payment_intent_id || meta?.payment_intent_id || meta?.stripe_payment_intent_id || "",
  ).trim();
  if (paymentIntentId) return `pi:${paymentIntentId}`;

  const sessionId = String(row?.stripe_session_id || meta?.stripe_session_id || "").trim();
  if (sessionId) return `session:${sessionId}`;

  const paymentLinkId = String(meta?.payment_link_id || "").trim();
  if (paymentLinkId) return `plink:${paymentLinkId}`;

  return `row:${String(row?.id || "").trim()}`;
}

function pharmacyPaymentRowTimestamp(row = {}) {
  const updated = new Date(row?.updated_at || row?.created_at || 0).getTime();
  if (Number.isFinite(updated)) return updated;
  return 0;
}

function pharmacyPaymentStatusRank(row = {}) {
  const status = normalizePharmacyPaymentStatus(row);
  if (status === "ready_for_pickup") return 6;
  if (status === "paid") return 5;
  if (status === "refunded") return 4;
  if (status === "refund_pending") return 3;
  if (status === "awaiting_payment") return 2;
  return 1;
}

function canonicalizePharmacyPaymentRows(rows = []) {
  const canonical = new Map();

  for (const row of Array.isArray(rows) ? rows : []) {
    const meta = row?.meta && typeof row.meta === "object" ? row.meta : {};
    if (meta?.detached_prescription_id) continue;

    const key = pharmacyPaymentCanonicalKey(row);
    const current = canonical.get(key);
    if (!current) {
      canonical.set(key, row);
      continue;
    }

    const currentTime = pharmacyPaymentRowTimestamp(current);
    const nextTime = pharmacyPaymentRowTimestamp(row);

    if (nextTime > currentTime) {
      canonical.set(key, row);
      continue;
    }

    if (nextTime === currentTime && pharmacyPaymentStatusRank(row) > pharmacyPaymentStatusRank(current)) {
      canonical.set(key, row);
    }
  }

  return Array.from(canonical.values()).sort((left, right) => {
    const byUpdated = pharmacyPaymentRowTimestamp(right) - pharmacyPaymentRowTimestamp(left);
    if (byUpdated !== 0) return byUpdated;
    return pharmacyPaymentStatusRank(right) - pharmacyPaymentStatusRank(left);
  });
}

async function resolveAccessiblePharmacyOwnerScope({
  currentUserId = "",
  currentUserEmail = "",
  requireCanonicalOwner = false,
} = {}) {
  const cleanUserId = String(currentUserId || "").trim();
  const cleanEmail = normalizeEmail(currentUserEmail);
  const ownerIds = new Set(cleanUserId ? [cleanUserId] : []);
  const ownsProfile = cleanUserId
    ? await hasPersistedPharmacyOwnerProfile(cleanUserId).catch(() => false)
    : false;

  if (ownsProfile) {
    return {
      ownerIds: Array.from(ownerIds),
      ownsProfile: true,
      memberLinked: false,
      reportingUnavailable: false,
      error: null,
    };
  }

  let accessibleRows = [];
  try {
    accessibleRows = await listAccessiblePharmacyStaffInvites();
  } catch (error) {
    return {
      ownerIds: Array.from(ownerIds),
      ownsProfile: false,
      memberLinked: false,
      reportingUnavailable: requireCanonicalOwner && Boolean(cleanUserId || cleanEmail),
      error,
    };
  }

  let memberLinked = false;
  (Array.isArray(accessibleRows) ? accessibleRows : []).forEach((row) => {
    const status = String(row?.status || "").trim().toLowerCase();
    const ownerUserId = String(row?.owner_user_id || "").trim();
    const memberUserId = String(row?.member_user_id || "").trim();
    const invitedEmail = normalizeEmail(row?.invited_email);
    const matchesMember =
      (cleanUserId && memberUserId === cleanUserId) ||
      (cleanEmail && invitedEmail === cleanEmail);

    if (!ownerUserId || !matchesMember || status !== "active") return;
    ownerIds.add(ownerUserId);
    if (status === "active") memberLinked = true;
  });

  const resolvedOwnerIds = Array.from(ownerIds).filter(Boolean);
  const onlySelfScope =
    cleanUserId && resolvedOwnerIds.length === 1 && resolvedOwnerIds[0] === cleanUserId;

  return {
    ownerIds: resolvedOwnerIds,
    ownsProfile: false,
    memberLinked,
    reportingUnavailable: requireCanonicalOwner && onlySelfScope,
    error: null,
  };
}

async function resolveAccessiblePharmacyOwnerIds({ currentUserId = "", currentUserEmail = "" } = {}) {
  const scope = await resolveAccessiblePharmacyOwnerScope({
    currentUserId,
    currentUserEmail,
  });
  return scope.ownerIds;
}

function createUnavailablePharmacySalesSnapshot(profile, reason = "owner_scope_unavailable") {
  return {
    hasLiveData: false,
    reportingUnavailable: true,
    reportingUnavailableReason: reason,
    soldItems: null,
    medicineRevenue: null,
    deliveryCollected: null,
    afrimedFee: null,
    completedCount: null,
    consultationsCount: null,
    monthlyRevenueSeries: [],
    topMedicines: [],
    deliveryEnabled: Boolean(profile?.deliveryEnabled),
  };
}

export async function loadPharmacyRoutingQueue(
  profile,
  { inventoryRows = [], fallbackQueue = [], limit = 8, currentUserId = "", currentUserEmail = "" } = {},
) {
  try {
    const ownerIdList = await resolveAccessiblePharmacyOwnerIds({ currentUserId, currentUserEmail });
    const ownerIds = new Set(ownerIdList);
    if (!ownerIds.size) {
      return (Array.isArray(fallbackQueue) ? fallbackQueue : [])
        .slice(0, limit)
        .map((row, index) => normalizeFallbackQueueRow(row, profile, inventoryRows, index));
    }

    const { data: fulfillmentRows, error: fulfillmentError } = await supabase
      .from("prescription_fulfillments")
      .select(`
        id,
        prescription_id,
        pharmacy_id,
        conversation_id,
        status,
        price,
        currency,
        created_at,
        updated_at,
        prescription:prescription_id (
          id,
          appointment_id,
          external_rx_id,
          doctor_user_id,
          patient_user_id,
          issued_at,
          created_at,
          meta
        )
      `)
      .in("pharmacy_id", ownerIdList)
      .in("status", ["pharmacy_selected", "preparing", "awaiting_payment", "paid", "ready_for_pickup"])
      .order("updated_at", { ascending: false })
      .order("created_at", { ascending: false })
      .limit(limit * 4);

    if (fulfillmentError) throw fulfillmentError;

    const prescriptions = (Array.isArray(fulfillmentRows) ? fulfillmentRows : [])
      .map((row) => (Array.isArray(row?.prescription) ? row.prescription[0] || null : row?.prescription || null))
      .filter(Boolean);
    const appointmentIds = Array.from(
      new Set(
        prescriptions
          .map((row) => String(row?.appointment_id || "").trim())
          .filter(Boolean),
      ),
    );
    const appointmentRowsRes = appointmentIds.length
      ? await supabase
          .from("appointments")
          .select("id, patient_id, care_subject_type, care_subject_dependant_id, care_subject_name, care_subject_relationship, care_subject_date_of_birth, guardian_user_id, booked_by_user_id, responsible_user_id, responsible_relationship")
          .in("id", appointmentIds)
      : { data: [] };
    const appointmentIdentityById = await loadPatientIdentityContextsByAppointmentId(
      appointmentRowsRes?.data || [],
      supabase,
    ).catch(() => new Map());
    const patientContextByUserId = await loadPatientContexts(
      supabase,
      prescriptions,
    ).catch(() => new Map());
    const doctorContextByUserId = await loadDoctorContexts(supabase, prescriptions).catch(() => new Map());

    const queue = (Array.isArray(fulfillmentRows) ? fulfillmentRows : [])
      .map((row, index) =>
        createQueueRowFromFulfillment(
          row,
          Array.isArray(row?.prescription) ? row.prescription[0] || {} : row?.prescription || {},
          profile,
          inventoryRows,
          patientContextByUserId,
          doctorContextByUserId,
          appointmentIdentityById,
          index,
        ),
      )
      .filter((row) => row.items && row.fulfillmentId)
      .slice(0, limit);

    if (queue.length > 0) return queue;
  } catch {
    // Fall back to preview queue.
  }

  return (Array.isArray(fallbackQueue) ? fallbackQueue : [])
    .slice(0, limit)
    .map((row, index) => normalizeFallbackQueueRow(row, profile, inventoryRows, index));
}

export async function loadPharmacySalesSnapshot(
  profile,
  { inventoryRows = [], currentUserId = "", currentUserEmail = "" } = {},
) {
  try {
    const ownerScope = await resolveAccessiblePharmacyOwnerScope({
      currentUserId,
      currentUserEmail,
      requireCanonicalOwner: true,
    });
    const ownerIds = ownerScope.ownerIds;

    if (ownerScope.reportingUnavailable) {
      if (typeof console !== "undefined") {
        console.warn("[AfriMed pharmacy] Branch reporting owner scope unavailable", {
          currentUserId,
          currentUserEmail,
          reason: ownerScope.error?.message || "member owner link unresolved",
        });
      }
      return createUnavailablePharmacySalesSnapshot(
        profile,
        ownerScope.error ? "owner_scope_lookup_failed" : "owner_scope_unresolved",
      );
    }

    if (!ownerIds.length && !String(currentUserEmail || "").trim()) {
      return {
        hasLiveData: false,
        soldItems: null,
        medicineRevenue: null,
        deliveryCollected: null,
        afrimedFee: null,
        completedCount: null,
        consultationsCount: null,
        monthlyRevenueSeries: [],
        topMedicines: [],
        deliveryEnabled: Boolean(profile?.deliveryEnabled),
      };
    }

    const { data, error } = await supabase
      .from("payments")
      .select(
        "id,appointment_id,status,amount,amount_cents,medicine_subtotal_cents,delivery_fee_cents,platform_fee_amount_cents,seller_net_amount_cents,pharmacy_owner_user_id,prescription_id,created_at,updated_at,meta",
      )
      .eq("type", "pharmacy")
      .in("pharmacy_owner_user_id", ownerIds)
      .order("updated_at", { ascending: false })
      .order("created_at", { ascending: false })
      .limit(200);

    if (error) throw error;

    const canonicalRows = canonicalizePharmacyPaymentRows(Array.isArray(data) ? data : []);
    const settledRows = canonicalRows.filter((row) =>
      isSettledPharmacyPaymentStatus(row?.status),
    );

    const totals = settledRows.reduce(
      (acc, row) => {
        const meta = row?.meta && typeof row.meta === "object" ? row.meta : {};
        const appointmentId =
          String(row?.appointment_id || meta?.appointment_id || meta?.appointmentId || "").trim();
        acc.soldItems += Math.max(
          readMinorInt(meta?.prescription_item_count, meta?.items_count, meta?.item_count, 1),
          1,
        );
        acc.medicineRevenue += readMinorInt(
          row?.medicine_subtotal_cents,
          meta?.medicine_subtotal_cents,
          meta?.medicine_subtotal,
        );
        acc.deliveryCollected += readMinorInt(
          row?.delivery_fee_cents,
          meta?.delivery_fee_cents,
          meta?.delivery_fee,
        );
        acc.afrimedFee += readMinorInt(
          row?.platform_fee_amount_cents,
          meta?.platform_fee_amount_cents,
          meta?.application_fee_amount_cents,
          meta?.application_fee_amount,
        );
        if (appointmentId) acc.consultationIds.add(appointmentId);
        return acc;
      },
      {
        soldItems: 0,
        medicineRevenue: 0,
        deliveryCollected: 0,
        afrimedFee: 0,
        consultationIds: new Set(),
      },
    );

    return {
      hasLiveData: canonicalRows.length > 0,
      reportingUnavailable: false,
      reportingUnavailableReason: "",
      soldItems: totals.soldItems ? totals.soldItems : 0,
      medicineRevenue: totals.medicineRevenue / 100,
      deliveryCollected: totals.deliveryCollected / 100,
      afrimedFee: totals.afrimedFee / 100,
      completedCount: settledRows.length,
       consultationsCount: totals.consultationIds.size,
      monthlyRevenueSeries: createMonthlyRevenueSeries(settledRows),
      topMedicines: createTopMedicineRows(settledRows, inventoryRows),
      deliveryEnabled: Boolean(profile?.deliveryEnabled),
    };
  } catch (error) {
    if (typeof console !== "undefined") {
      console.warn("[AfriMed pharmacy] Branch reporting load failed", {
        currentUserId,
        currentUserEmail,
        reason: error?.message || "unknown",
      });
    }
    return createUnavailablePharmacySalesSnapshot(profile, "reporting_load_failed");
  }
}

export async function listPharmacyPaymentTransactions({
  currentUserId = "",
  currentUserEmail = "",
  limit = 24,
} = {}) {
  const ownerScope = await resolveAccessiblePharmacyOwnerScope({
    currentUserId,
    currentUserEmail,
    requireCanonicalOwner: true,
  });
  if (ownerScope.reportingUnavailable) {
    throw ownerScope.error || new Error("Branch reporting owner scope is unavailable.");
  }
  const ownerIds = ownerScope.ownerIds;
  if (!ownerIds.length) return [];

  const { data, error } = await supabase
    .from("payments")
    .select(
      "id,type,status,amount,amount_cents,currency,method,prescription_id,pharmacy_owner_user_id,medicine_subtotal_cents,delivery_fee_cents,platform_fee_amount_cents,seller_net_amount_cents,stripe_payment_intent_id,created_at,updated_at,meta",
    )
    .eq("type", "pharmacy")
    .in("pharmacy_owner_user_id", ownerIds)
    .order("updated_at", { ascending: false })
    .order("created_at", { ascending: false })
    .limit(limit * 6);

  if (error) throw error;

  const rows = canonicalizePharmacyPaymentRows(Array.isArray(data) ? data : []).slice(0, limit);
  const ids = [...new Set(rows.map(row => row.prescription_id).filter(Boolean))];
  const identities = new Map();
  if (ids.length) {
    const {data: prescriptions, error: identityError} = await supabase.from("prescriptions")
      .select("id,external_rx_id,meta").in("id", ids);
    if (identityError) throw identityError;
    for (const row of prescriptions || []) identities.set(row.id, prescriptionIdentifier(row));
  }
  return rows.map((row) => {
    const meta = row?.meta && typeof row.meta === "object" ? row.meta : {};
    const grossAmountCents = readMinorInt(
      row?.amount_cents,
      row?.amount,
      meta?.gross_amount_cents,
      meta?.gross_amount,
    );
    const medicineSubtotalCents = readMinorInt(
      row?.medicine_subtotal_cents,
      meta?.medicine_subtotal_cents,
      meta?.medicine_subtotal,
    );
    const deliveryFeeCents = readMinorInt(
      row?.delivery_fee_cents,
      meta?.delivery_fee_cents,
      meta?.delivery_fee,
    );
    const afrimedFeeCents = readMinorInt(
      row?.platform_fee_amount_cents,
      meta?.platform_fee_amount_cents,
      meta?.application_fee_amount_cents,
      meta?.application_fee_amount,
    );
    const pharmacyNetCents = readMinorInt(
      row?.seller_net_amount_cents,
      meta?.seller_net_amount_cents,
      meta?.seller_net_amount,
      Math.max(grossAmountCents - afrimedFeeCents, 0),
    );

    return {
      id: String(row?.id || "").trim(),
      createdAt: String(row?.created_at || row?.updated_at || "").trim(),
      updatedAt: String(row?.updated_at || row?.created_at || "").trim(),
      status: normalizePharmacyPaymentStatus(row),
      currency: resolvePharmacyCurrency({
        explicitCurrency: row?.currency || meta?.currency,
        meta,
      }),
      method: String(row?.method || meta?.payment_method_type || "card").trim(),
      reference: identities.get(row.prescription_id) || String(row.prescription_id || row.stripe_payment_intent_id || row.id || ""),
      prescriptionId: String(row?.prescription_id || "").trim(),
      paymentIntentId: String(row?.stripe_payment_intent_id || "").trim(),
      patientName: pickFirst(
        meta?.patient_name,
        meta?.patientName,
        meta?.patient_full_name,
        meta?.service_name,
        meta?.serviceName,
        meta?.description,
        meta?.sponsor_name,
      ),
      doctorName: pickFirst(
        meta?.doctor_name,
        meta?.doctorName,
        meta?.doctor_full_name,
        meta?.doctorName,
        meta?.sponsor_email ? `Sponsor • ${meta.sponsor_email}` : "",
        meta?.seller_name ? `Branch • ${meta.seller_name}` : "",
      ),
      pharmacyName: pickFirst(
        meta?.pharmacy_name,
        meta?.pharmacyName,
      ),
      medicineSubtotal: medicineSubtotalCents / 100,
      deliveryFee: deliveryFeeCents / 100,
      afrimedFee: afrimedFeeCents / 100,
      pharmacyNet: pharmacyNetCents / 100,
      grossAmount: grossAmountCents / 100,
    };
  });
}

export function syncPharmacyNotifications({
  queue = [],
  inventoryRows = [],
  profile = null,
} = {}) {
  const existingNotifications = readPharmacyNotifications();
  const existing = new Map(
    existingNotifications.map((item) => [String(item.id || "").trim(), item]),
  );

  const routingAlerts = (Array.isArray(queue) ? queue : []).slice(0, 12).map((row) => {
    const id = `pharmacy-routing:${String(row.prescriptionId || row.id || "").trim()}`;
    const prior = existing.get(id);
    const title = row.hasInventoryMatch
      ? "Prescription routed to this branch"
      : "Stock attention needed";
    const message = row.hasInventoryMatch
      ? `${row.patient} can be served here for ${row.items}${profile?.deliveryEnabled ? " with delivery support ready." : "."}`
      : `${row.items} needs stock confirmation before this branch should accept the handoff.`;

    return {
      id,
      kind: row.hasInventoryMatch ? "routing" : "stock-warning",
      title,
      message,
      priority: "important",
      source: "pharmacy",
      audience: "pharmacy",
      channels: {
        in_app: true,
        push: false,
        sound: false,
        vibration: false,
      },
      quiet_by_default: true,
      escalation_eligible: true,
      group_key: row.hasInventoryMatch
        ? "pharmacy:routing"
        : "pharmacy:stock-warning",
      group_title: row.hasInventoryMatch
        ? "Prescription routing updates"
        : "Stock attention needed",
      group_message: row.hasInventoryMatch
        ? "{count} routed prescriptions need review in this branch."
        : "{count} prescriptions need stock confirmation before acceptance.",
      read: Boolean(prior?.read),
      createdAt: row.issuedAt || new Date().toISOString(),
      prescriptionId: row.prescriptionId || "",
      route: "/pharmacy/prescriptions",
      patient: row.patient,
    };
  });

  const lowStockAlerts = buildLowStockNotifications(inventoryRows, existing);
  const next = [...routingAlerts, ...lowStockAlerts];

  if (
    next.length === 0 &&
    existingNotifications.some((item) =>
      item?.source === "pharmacy" &&
      (item?.kind === "routing" || item?.kind === "stock-warning")
    )
  ) {
    return existingNotifications;
  }

  writeJson(PHARMACY_NOTIFICATIONS_STORAGE_KEY, next);
  window.dispatchEvent(new Event(PHARMACY_NOTIFICATIONS_CHANGED_EVENT));
  return next;
}

export async function recomputeStockAlerts({
  profile = null,
  inventoryRows = [],
  fallbackQueue = [],
  limit = 8,
  currentUserId = "",
  currentUserEmail = "",
  shouldCommit = () => true,
} = {}) {
  if (!String(currentUserId || "").trim() && !normalizeEmail(currentUserEmail)) {
    const existingNotifications = readPharmacyNotifications();
    return {
      queue: Array.isArray(fallbackQueue) ? fallbackQueue : [],
      notifications: existingNotifications,
    };
  }

  const nextQueue = await loadPharmacyRoutingQueue(profile, {
    inventoryRows,
    fallbackQueue,
    limit,
    currentUserId,
    currentUserEmail,
  });

  if (!shouldCommit()) return { queue: [], notifications: [] };

  const nextNotifications = syncPharmacyNotifications({
    queue: nextQueue,
    inventoryRows,
    profile,
  });

  return {
    queue: nextQueue,
    notifications: nextNotifications,
  };
}

export function syncPharmacyNotificationsFromQueue(queue = [], profile = null) {
  return syncPharmacyNotifications({
    queue,
    inventoryRows: [],
    profile,
  });
}
